import React from 'react';
import { formatMinistryMonthLabel } from '../../utils/mbcbReportPeriodLabels';
import type { GivingBreakdown } from '../../utils/mbcbReportShared';
import { AiAssistPill, NarrativeField } from './EditorShell';
import { CurrencyField } from './CurrencyField';

interface StepGenerosityProps {
  ministryMonth: number;
  ministryYear: number;
  additionalNotes: string;
  setAdditionalNotes: (v: string) => void;
  givingNotes: string;
  setGivingNotes: (v: string) => void;
  nextMonthFocus: string;
  setNextMonthFocus: (v: string) => void;
  giving: GivingBreakdown;
  setGiving: React.Dispatch<React.SetStateAction<GivingBreakdown>>;
  budgetReceiptsTotal: number;
  missionGivingTotal: number;
  canEdit: boolean;
  onOpenAi: () => void;
  onFieldChange?: () => void;
}

const incomeFields: { key: keyof GivingBreakdown['income']; label: string }[] = [
  { key: 'congregationalOffering', label: 'Congregational offering' },
  { key: 'sponsoringChurches', label: 'Sponsoring church(es)' },
  { key: 'associations', label: 'Association(s)' },
  { key: 'mbcb', label: 'MBCB support' },
  { key: 'otherSupport', label: 'Other support' },
];

const missionFields: { key: keyof GivingBreakdown['missionGiving']; label: string }[] = [
  { key: 'cooperativeProgram', label: 'Cooperative Program' },
  { key: 'associations', label: 'Association(s)' },
  { key: 'margaretLackey', label: 'Margaret Lackey' },
  { key: 'otherMissionCauses', label: 'Other mission causes' },
];

export const StepGenerosity: React.FC<StepGenerosityProps> = ({
  ministryMonth,
  ministryYear,
  additionalNotes,
  setAdditionalNotes,
  givingNotes,
  setGivingNotes,
  nextMonthFocus,
  setNextMonthFocus,
  giving,
  setGiving,
  budgetReceiptsTotal,
  missionGivingTotal,
  canEdit,
  onOpenAi,
  onFieldChange,
}) => {
  const ministryLabel = formatMinistryMonthLabel(ministryMonth, ministryYear);

  const setIncome = (key: keyof GivingBreakdown['income'], value: number | null) => {
    setGiving((g) => ({
      ...g,
      income: { ...g.income, [key]: value },
    }));
    onFieldChange?.();
  };

  const setMission = (key: keyof GivingBreakdown['missionGiving'], value: number | null) => {
    setGiving((g) => ({
      ...g,
      missionGiving: { ...g.missionGiving, [key]: value },
    }));
    onFieldChange?.();
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto px-4 py-5 space-y-5 animate-step-in">
      <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50/50 border border-emerald-100 px-4 py-4">
        <h2 className="text-lg font-bold text-gray-900">Generosity &amp; giving</h2>
        <p className="text-sm text-gray-600 mt-1">
          Where did you see generosity in {ministryLabel}?
        </p>
        {canEdit && (
          <div className="mt-3">
            <AiAssistPill onClick={onOpenAi} />
          </div>
        )}
      </div>

      <NarrativeField
        label="Generosity stories"
        hint="How did you see generosity — given or received?"
        value={additionalNotes}
        onChange={(v) => {
          setAdditionalNotes(v);
          onFieldChange?.();
        }}
        disabled={!canEdit}
        rows={3}
      />

      <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">Budget receipts</h3>
        <div className="space-y-3">
          {incomeFields.map(({ key, label }) => (
            <CurrencyField
              key={key}
              label={label}
              value={giving.income[key]}
              onChange={(v) => setIncome(key, v)}
              disabled={!canEdit}
            />
          ))}
        </div>
        <div className="flex items-center justify-between rounded-xl bg-emerald-50/60 border border-emerald-100 px-4 py-2.5 mt-2">
          <span className="text-sm text-gray-600">Total budget receipts</span>
          <span className="text-base font-semibold text-emerald-800 tabular-nums">
            ${budgetReceiptsTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </span>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">Mission giving</h3>
        <div className="space-y-3">
          {missionFields.map(({ key, label }) => (
            <CurrencyField
              key={key}
              label={label}
              value={giving.missionGiving[key]}
              onChange={(v) => setMission(key, v)}
              disabled={!canEdit}
            />
          ))}
        </div>
        <div className="flex items-center justify-between rounded-xl bg-emerald-50/60 border border-emerald-100 px-4 py-2.5 mt-2">
          <span className="text-sm text-gray-600">Total mission giving</span>
          <span className="text-base font-semibold text-emerald-800 tabular-nums">
            ${missionGivingTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </span>
        </div>
      </div>

      <NarrativeField
        label="Giving notes (optional)"
        value={givingNotes}
        onChange={(v) => {
          setGivingNotes(v);
          onFieldChange?.();
        }}
        disabled={!canEdit}
        rows={2}
      />

      <NarrativeField
        label="Hope for next month (optional)"
        value={nextMonthFocus}
        onChange={(v) => {
          setNextMonthFocus(v);
          onFieldChange?.();
        }}
        disabled={!canEdit}
        rows={2}
      />
    </div>
  );
};
